import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { NotificationItem as NotifItem, NotificationLevel } from '../types';

const levelIcons: Record<NotificationLevel, React.ReactNode> = {
  info: <InfoOutlinedIcon fontSize="small" color="info" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
  error: <ErrorOutlineIcon fontSize="small" color="error" />,
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface Props {
  notification: NotifItem;
  onClick: () => void;
}

export default function NotificationItemComponent({ notification, onClick }: Props) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        bgcolor: notification.isRead ? undefined : 'action.hover',
        py: 1,
        px: 2,
      }}
      data-testid="notification-item"
    >
      <ListItemIcon sx={{ minWidth: 32 }}>{levelIcons[notification.level]}</ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" sx={{ fontWeight: notification.isRead ? 400 : 600 }}>
            {notification.title}
          </Typography>
        }
        secondary={
          <>
            {notification.message && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                component="span"
                sx={{ display: 'block' }}
              >
                {notification.message}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled" component="span">
              {formatRelativeTime(notification.createdAt)}
            </Typography>
          </>
        }
      />
    </ListItemButton>
  );
}
